// Inline candidate-profile fetcher. Backed by GET /candidates/:id/profile
// on the agentforge server — returns CRM record + parsed resumes in one
// shot so the candidate-row "show more" expansion can render without
// going through the agent loop.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { callAgentForge } from './http';

export interface ResumePayload {
  id: number;
  file_name?: string;
  content_type?: string;
  url?: string;
  is_primary?: boolean;
  parsed_content?: {
    Name?: string;
    Emails?: string[];
    Country?: string;
    Location?: string | null;
    SkillTags?: string[];
    LinkedInURL?: string | null;
    GithubURL?: string[];
    MobileNumber?: string;
    CurrentJobTitle?: string;
    TotalExperienceInYears?: number;
    Experience?: {
      Company?: string;
      JobTitle?: string;
      From?: string;
      To?: string | null;
      Notes?: string;
      IsCurrentJob?: number | boolean;
    }[];
    Education?: {
      School?: string;
      Discipline?: string;
      Qualification?: string;
      From?: string;
      To?: string | null;
    }[];
    Certifications?: string[];
  };
  ats_applications?: {
    id: number;
    status?: string;
    job?: { id: number; name: string };
  }[];
}

export interface CandidateProfileResponse {
  contact_id: number;
  contact: Record<string, unknown>;
  resumes: ResumePayload[];
}

const candidateProfileKey = (contactId: string | number) =>
  ['agentforge', 'candidate-profile', String(contactId)] as const;

export const useCandidateProfileQuery = (
  contactId: string | number | null,
  enabled: boolean
): UseQueryResult<CandidateProfileResponse, Error> =>
  useQuery({
    queryKey: candidateProfileKey(contactId ?? ''),
    enabled: enabled && contactId !== null && String(contactId).length > 0,
    queryFn: ({ signal }) =>
      callAgentForge<CandidateProfileResponse>({
        url: `/candidates/${contactId}/profile`,
        signal,
      }),
    staleTime: 60_000, // record changes slowly; one minute is fine
  });
